// Catálogo de productos con listas de precios: lectura de archivos CSV (Excel → "Guardar como CSV") y validación.
// Todo lo que se importa se revisa aquí antes de tocar la base de datos.

export const MAX_IMPORT_ROWS = 2000;
export const BATCH_SIZE = 200;
export const DEFAULT_LIST_KEY = 'general';

export const norm = (v) => String(v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Clave corta y segura para una lista de precios: "Distribuidor Oro" → "distribuidor_oro".
export const slugKey = (v) => norm(v).replace(/ /g, '_').slice(0, 40);

// Lee texto CSV (comillas dobles, saltos de línea dentro de comillas, separador coma, punto y coma o tabulador).
export const parseCsv = (text) => {
  const src = String(text ?? '').replace(/^﻿/, '');
  const firstLine = src.split(/\r?\n/, 1)[0] || '';
  const counts = { ';': 0, ',': 0, '\t': 0 };
  let inQ = false;
  for (const ch of firstLine) {
    if (ch === '"') inQ = !inQ;
    else if (!inQ && ch in counts) counts[ch] += 1;
  }
  const delim = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
  const rows = [];
  let row = [];
  let cell = '';
  inQ = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQ) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i += 1; }
      else if (ch === '"') inQ = false;
      else cell += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === delim) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i += 1;
      row.push(cell); cell = '';
      rows.push(row); row = [];
    } else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  // Solo se quitan las filas vacías del final: las del medio se conservan para que el número de línea sea el del archivo.
  while (rows.length && rows[rows.length - 1].every((c) => String(c).trim() === '')) rows.pop();
  return rows;
};

// Precio escrito a la colombiana o a la gringa: "$ 12.500", "12.500,50", "12,500.50", "12500". Devuelve null si no es un precio.
export const parsePrice = (raw) => {
  // Un número que viene de Excel ya es un número: no se interpreta como texto (12.345 no es "doce mil").
  if (typeof raw === 'number') return Number.isFinite(raw) && raw >= 0 ? Math.round(raw * 100) / 100 : null;
  let s = String(raw ?? '').replace(/[^\d.,-]/g, '');
  if (!s || s === '-' ) return null;
  if (s.startsWith('-')) return null;
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  if (lastDot !== -1 && lastComma !== -1) {
    const decimal = lastDot > lastComma ? '.' : ',';
    const thousands = decimal === '.' ? ',' : '.';
    s = s.split(thousands).join('').replace(decimal, '.');
  } else if (lastDot !== -1 || lastComma !== -1) {
    const sep = lastDot !== -1 ? '.' : ',';
    const parts = s.split(sep);
    // Un solo separador seguido de exactamente 3 dígitos es de miles ("12.500"); si no, es decimal ("12.5", "12,50").
    const isThousands = parts.length > 2 || (parts.length === 2 && parts[1].length === 3 && parts[0] !== '' && parts[0] !== '0');
    s = isThousands ? parts.join('') : `${parts[0]}.${parts[1]}`;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
};

const SKU = ['sku', 'codigo', 'cod', 'referencia', 'ref', 'codigo producto', 'code'];
const NAME = ['nombre', 'producto', 'descripcion', 'articulo', 'item', 'name', 'nombre producto'];
const CATEGORY = ['categoria', 'familia', 'grupo', 'linea', 'category'];
const UNIT = ['unidad', 'und', 'um', 'unidad de medida', 'unit'];
const STOCK = ['existencias', 'existencia', 'stock', 'inventario', 'cantidad', 'saldo', 'saldo cantidades'];
const ACTIVE = ['activo', 'estado', 'active'];

const findCol = (headers, aliases) => headers.findIndex((h) => aliases.includes(h));
const isPriceHeader = (h) => /^(precio|lista|price|tarifa|pvp)\b/.test(h);

// Columnas del listado «Productos / Servicios» que exporta Siigo (Inventario → Productos y servicios → Exportar). Las que vienen después de «Stock mínimo»
// son las listas de precios, con el nombre que les dio la empresa (ej. INTEGRADOR, LOCAL, CLIENTE FINAL).
const SIIGO_KNOWN = ['tipo', 'codigo', 'nombre', 'referencia fabrica', 'categoria', 'estado', 'impuesto cargo', 'impuesto cargo 2', 'descripcion larga', 'es incluido',
  'inventariable', 'saldo cantidades', 'visible en facturas de venta', 'stock minimo', 'unidad de medida', 'impuesto retencion', 'costo', 'costo promedio'];
const isSiigoHeader = (headers) => headers.includes('codigo') && headers.includes('nombre') && ['es incluido', 'inventariable', 'impuesto cargo', 'visible en facturas de venta'].some((h) => headers.includes(h));

// Los reportes de Siigo traen título, empresa y NIT antes de los encabezados: se busca la primera fila que parezca de títulos.
export const findHeaderRow = (rows) => {
  const limit = Math.min(rows.length, 40);
  for (let i = 0; i < limit; i += 1) {
    const cells = (rows[i] || []).map(norm).filter(Boolean);
    if (cells.length >= 3 && cells.some((c) => NAME.includes(c))) return i;
  }
  return 0;
};

// "IVA 19%" → { tax_kind: 'iva', tax_rate: 19 }; "Excluido" → { tax_kind: 'excluido', tax_rate: 0 }; vacío o desconocido → null.
export const parseTax = (raw) => {
  const text = norm(raw);
  if (!text) return null;
  const rate = text.match(/(\d+(?: \d+)?)\s*$/);
  const pct = String(raw).match(/(\d+(?:[.,]\d+)?)\s*%/);
  const value = pct ? Number(pct[1].replace(',', '.')) : (rate ? Number(rate[1].replace(' ', '.')) : null);
  if (/^(excluido|no causa)/.test(text)) return { tax_kind: 'excluido', tax_rate: 0 };
  if (/^exento/.test(text)) return { tax_kind: 'exento', tax_rate: 0 };
  if (/(consumo|inc\b|impoconsumo)/.test(text) && value !== null && value <= 100) return { tax_kind: 'consumo', tax_rate: value };
  if (/^iva/.test(text) && value !== null && value <= 100) return { tax_kind: 'iva', tax_rate: value };
  return null;
};

const isYes = (v) => ['si', 'yes', 'true', '1', 'x'].includes(norm(v));
const cellText = (v) => String(v ?? '').trim();
// Precio sin el impuesto: 53.300 con IVA 19 % → 44.789,92 (la cotización vuelve a sumar el IVA y da 53.300).
export const removeTax = (price, rate) => Math.round((price / (1 + rate / 100)) * 100) / 100;

// Convierte las filas leídas (de un CSV o de un Excel) en productos con sus precios. Las columnas de precio son las que empiezan por
// "precio", "lista" o "tarifa": "Precio" → lista general; "Precio mayorista" → lista mayorista. En el listado de Siigo, cada columna
// después de «Stock mínimo» es una lista de precios. `options.pricesIncludeTax === false` guarda los precios de Siigo tal cual (sin quitarles el IVA).
const FOOTER_ROW = /^procesado\s+en\b/i;

export const buildImport = (csvRows, options = {}) => {
  if (!Array.isArray(csvRows) || csvRows.length < 2) {
    return { ok: false, error: 'El archivo no tiene filas. La primera fila debe traer los títulos de las columnas.' };
  }
  const start = findHeaderRow(csvRows);
  const headerRow = csvRows[start] || [];
  const headers = headerRow.map(norm);
  const nameCol = findCol(headers, NAME);
  if (nameCol === -1) return { ok: false, error: 'No encontré la columna del nombre. Ponle el título "Nombre" o "Producto".' };
  const siigo = isSiigoHeader(headers);
  const skuCol = findCol(headers, SKU);
  const catCol = findCol(headers, CATEGORY);
  const unitCol = findCol(headers, UNIT);
  const stockCol = findCol(headers, STOCK);
  const activeCol = findCol(headers, ACTIVE);
  const brandCol = siigo ? headers.indexOf('referencia fabrica') : -1;
  const taxCol = siigo ? headers.indexOf('impuesto cargo') : -1;
  const tax2Col = siigo ? headers.indexOf('impuesto cargo 2') : -1;
  const includedCol = siigo ? headers.indexOf('es incluido') : -1;
  const inventoryCol = siigo ? headers.indexOf('inventariable') : -1;

  const dataRows = csvRows.slice(start + 1);
  const lists = [];
  const addList = (idx, label) => {
    const key = slugKey(label) || DEFAULT_LIST_KEY;
    if (lists.some((l) => l.key === key)) return;
    lists.push({ key, name: label ? label.replace(/\b\w/g, (c) => c.toUpperCase()) : 'General', col: idx });
  };
  const lastKnown = siigo ? Math.max(...headers.map((h, i) => (SIIGO_KNOWN.includes(h) ? i : -1))) : -1;
  headers.forEach((h, idx) => {
    if (idx === nameCol) return;
    if (isPriceHeader(h)) addList(idx, h.replace(/^(precio|lista|price|tarifa|pvp)\b\s*/, '').trim());
    // En Siigo, cada columna con título después de las conocidas es una lista, si trae algún precio.
    else if (siigo && idx > lastKnown && h && dataRows.some((r) => parsePrice(r[idx]) !== null && cellText(r[idx]) !== '')) addList(idx, h);
  });
  if (!lists.length) return { ok: false, error: 'No encontré ninguna columna de precio. Ponle el título "Precio" (o "Precio mayorista", "Precio distribuidor"...).' };

  if (dataRows.length > MAX_IMPORT_ROWS) {
    return { ok: false, error: `El archivo tiene ${dataRows.length} filas; el máximo por importación es ${MAX_IMPORT_ROWS}. Divídelo en partes.` };
  }

  const products = [];
  const errors = [];
  const seen = new Set();
  const removeIva = options.pricesIncludeTax !== false;
  let includedRows = 0;
  let example = null;
  dataRows.forEach((r, i) => {
    const line = start + i + 2; // número de fila del archivo (Excel cuenta desde 1)
    if (r.every((c) => cellText(c) === '')) return; // fila vacía: se ignora sin avisar
    // El reporte de Siigo termina con «Procesado en: octubre 06 2026 15:29»: no es un producto (y su fecha se leía como un precio de 620.261.529).
    if (FOOTER_ROW.test(cellText(r[nameCol])) || FOOTER_ROW.test(cellText(r[0]))) return;
    const baseName = cellText(r[nameCol]).replace(/\s+/g, ' ');
    if (!baseName) { errors.push({ line, message: 'Sin nombre: se omitió.' }); return; }
    // La marca (Referencia fábrica) se agrega al nombre: así el bot y las cotizaciones distinguen "COLLECTIVE 12I - INTELBRAS".
    const brand = brandCol === -1 ? '' : cellText(r[brandCol]).replace(/\s+/g, ' ');
    const name = brand && !norm(baseName).includes(norm(brand)) ? `${baseName} - ${brand}` : baseName;
    const rawSku = skuCol === -1 ? '' : cellText(r[skuCol]).replace(/\s+/g, ' ');
    // Sin código, el producto se identifica por su nombre: reimportar el archivo lo actualiza en vez de duplicarlo.
    const sku = rawSku || `n-${slugKey(name)}`.slice(0, 60);
    const dedupe = sku.toLowerCase();
    if (seen.has(dedupe)) { errors.push({ line, message: `Código repetido en el archivo (${sku}): se omitió.` }); return; }
    seen.add(dedupe);

    const tax = taxCol === -1 ? null : parseTax(r[taxCol]);
    const includesTax = includedCol !== -1 && isYes(r[includedCol]) && tax && tax.tax_kind === 'iva' && tax.tax_rate > 0;
    const prices = {};
    let invalid = null;
    lists.forEach((l) => {
      if (cellText(r[l.col]) === '') return;
      const p = parsePrice(r[l.col]);
      if (p === null) invalid = `El precio "${cellText(r[l.col])}" de la lista ${l.name} no es válido.`;
      else if (p === 0 && siigo) return; // en Siigo, 0 es «sin precio en esta lista»
      else prices[l.key] = includesTax && removeIva ? removeTax(p, tax.tax_rate) : p;
    });
    if (invalid) { errors.push({ line, message: `${invalid} Se omitió.` }); return; }
    if (!Object.keys(prices).length) { errors.push({ line, message: 'Sin ningún precio: se omitió.' }); return; }
    if (includesTax) {
      includedRows += 1;
      if (!example) {
        const first = lists.find((l) => prices[l.key] !== undefined);
        example = { name, list: first.name, listed: parsePrice(r[first.col]), withoutTax: removeTax(parsePrice(r[first.col]), tax.tax_rate), rate: tax.tax_rate };
      }
    }

    let stock = null;
    const stockRaw = stockCol === -1 ? '' : r[stockCol];
    if (cellText(stockRaw) !== '' && !(inventoryCol !== -1 && !isYes(r[inventoryCol]))) {
      const s = typeof stockRaw === 'number' ? stockRaw : Number(String(stockRaw).replace(/\./g, '').replace(',', '.'));
      stock = Number.isFinite(s) && s >= 0 ? s : null;
    }
    let active = true;
    if (activeCol !== -1) {
      const a = norm(r[activeCol]);
      if (['no', 'inactivo', 'false', '0', 'agotado'].includes(a)) active = false;
    }
    const second = tax2Col === -1 ? '' : cellText(r[tax2Col]);
    if (second) errors.push({ line, message: `Tiene un segundo impuesto (${second}) que no se importa; el producto sí se importó con el primero.`, warning: true });
    products.push({
      sku, name: name.slice(0, 200),
      category: catCol === -1 ? null : (cellText(r[catCol]).slice(0, 100) || null),
      unit: unitCol === -1 ? null : (cellText(r[unitCol]).slice(0, 30) || null),
      stock, active, prices,
      ...(siigo ? { source: 'siigo' } : {}),
      ...(tax ? { tax_kind: tax.tax_kind, tax_rate: tax.tax_rate } : {})
    });
  });
  if (!products.length) return { ok: false, error: 'Ninguna fila tenía nombre y precio válidos.', errors };
  return { ok: true, products, lists: lists.map(({ key, name }) => ({ key, name })), errors, siigo, includedRows, pricesIncludeTax: removeIva, example, hasTax: products.some((p) => p.tax_kind !== undefined) };
};

// Tras importar: la lista predeterminada debe tener precios (el bot cae en ella cuando el cliente no tiene lista propia).
// Si la actual no tiene ninguno —ni de antes ni de este archivo— o no hay ninguna, devuelve la primera lista del archivo que sí trae precios; si no hay que cambiar nada, null.
export const listToMakeDefault = ({ lists = [], existingPricedKeys = new Set(), importedProducts = [] }) => {
  const imported = [];
  importedProducts.forEach((p) => Object.keys(p.prices || {}).forEach((k) => { if (!imported.includes(k)) imported.push(k); }));
  if (!imported.length) return null;
  const current = lists.find((l) => l.is_default);
  if (current && (existingPricedKeys.has(current.key) || imported.includes(current.key))) return null;
  return imported[0];
};

export const chunk = (arr, size = BATCH_SIZE) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export const formatPrice = (n) =>
  n === null || n === undefined ? '—' : `$${Number(n).toLocaleString('es-CO', { maximumFractionDigits: 2 })}`;

// Búsqueda en pantalla: todas las palabras deben aparecer (sin tildes ni mayúsculas) en código, nombre o categoría.
export const matchesSearch = (product, query) => {
  const terms = norm(query).split(' ').filter(Boolean);
  if (!terms.length) return true;
  const hay = norm(`${product.sku ?? ''} ${product.name ?? ''} ${product.category ?? ''}`);
  return terms.every((t) => hay.includes(t));
};
